// Supermove
import {gql} from '@supermove/graphql';
import {useEffect} from '@supermove/hooks';
import {UserModel} from '@supermove/models';

// Shared
import UserRole from '@shared/modules/User/enums/UserRole';

type PendingProps = {
  data?: never;
  handleLogOut: () => void;
};

type ReadyProps = {
  data: {
    isAuthValid: boolean;
    viewer: UserModel;
    actualViewer: UserModel;
  };
  handleLogOut: () => void;
};

const useLogOutUnauthorizedUser = ({data, handleLogOut}: PendingProps | ReadyProps) => {
  useEffect(() => {
    if (!data || !data.viewer) {
      return;
    }

    const {isAuthValid, viewer, actualViewer} = data;
    // First check if impersonating. We want to allow impersonating inactive users for super admins.
    if (actualViewer && viewer.id !== actualViewer.id) {
      // When a super admin is impersonating a user, log out only based on the actualViewer.
      if (actualViewer.role !== UserRole.SUPER_ADMIN || !actualViewer.isActive) {
        handleLogOut();
      }
      // If this is not an impersonation, logout removed users or anyone in an inactive company.
    } else if (
      viewer.isDeleted ||
      !viewer.isActive ||
      !viewer.viewingOrganization.company.isActive
    ) {
      handleLogOut();
      // isAuthValid checks against JWT claims if the user is viewing the correct organization.
    } else if (!isAuthValid) {
      handleLogOut();
    }
  }, [data, handleLogOut]);
};

useLogOutUnauthorizedUser.fragment = gql`
  fragment useLogOutUnauthorizedUser on Query {
    isAuthValid
    viewer {
      id
      isActive
      isDeleted
      viewingOrganization {
        id
        company {
          id
          isActive
        }
      }
    }
    actualViewer {
      id
      isActive
      role
    }
  }
`;

export default useLogOutUnauthorizedUser;
